import {
	Box,
	Group,
	Loader,
	Menu,
	ScrollArea,
	Stack,
	TextInput,
} from '@mantine/core';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { Text } from '@repo/foundations';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useCollectionList } from '../../../../../../../api';
import { SecodaEntityIcon } from '../../../../../../../components/SecodaEntity';
import { useHomepageTeam } from '../../../../../hooks/useHomepageTeam';

export interface ICollectionSelectorDropdownProps {
	onCollectionChange: (value: [string, string]) => void;
}

function CollectionSelectorDropdown({
	onCollectionChange,
}: ICollectionSelectorDropdownProps) {
	const { isTeamsPage, team } = useHomepageTeam();
	const [searchTerm, setSearchTerm] = useInputState('');
	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 200);

	const filters = useMemo(() => {
		const filtersToUse: Record<string, string> = {};

		if (isTeamsPage) {
			filtersToUse.teams = team.id;
		}

		if (debouncedSearchTerm) {
			filtersToUse.title__trigram_word_similar = debouncedSearchTerm;
		}

		return filtersToUse;
	}, [debouncedSearchTerm, isTeamsPage, team?.id]);

	const { isLoading, data } = useCollectionList({
		filters,
		options: {
			select: ({ results }) => results,
			placeholderData: keepPreviousData,
			refetchOnMount: 'always',
		},
	});

	const handleClick = (id: string, title: string) => () => {
		onCollectionChange([id, title]);
	};

	return (
		<Menu.Dropdown>
			<Stack spacing={0}>
				<TextInput
					p={0}
					mb="xs"
					placeholder="Search collections..."
					value={searchTerm}
					onChange={setSearchTerm}
					rightSection={isLoading && <Loader size={12} />}
					autoFocus
				/>
				<Box>
					{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
					{/* @ts-ignore poorly typed component by Mantine */}
					<ScrollArea.Autosize mah={200}>
						{data?.map((collection) => (
							<Menu.Item
								key={collection.id}
								onClick={handleClick(
									collection.id,
									collection.title || 'Untitled'
								)}
							>
								<Group spacing={8}>
									<SecodaEntityIcon entity={collection} size={16} />
									<Text lineClamp={1}>{collection.title}</Text>
								</Group>
							</Menu.Item>
						))}
					</ScrollArea.Autosize>
				</Box>
			</Stack>
		</Menu.Dropdown>
	);
}

export default CollectionSelectorDropdown;
